import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../../context/AuthContext'
import ImagePicker from '../../ondemand-library/content-catalog/ImagePicker'

// Resolve a sensible default title for a Mux asset:
//   meta.title → passthrough (filename) → asset.id
function defaultTitle(asset) {
  const t = asset.meta?.title?.trim()
  if (t) return t
  if (asset.passthrough) return asset.passthrough.replace(/\.[^.]+$/, '')
  return asset.id
}

function getPlaybackId(asset) {
  return asset?.playback_ids?.[0]?.id || null
}

// Build the body for POST /vod/create-linked from a Mux asset.
// thumbnailOverride: optional URL from CF Images; falls back to Mux auto-thumb.
function buildPayload(asset, title, thumbnailOverride) {
  const pid = getPlaybackId(asset)
  const muxThumb = pid ? `https://image.mux.com/${pid}/thumbnail.jpg?width=1280&height=720&time=5` : ''
  return {
    title: (title || defaultTitle(asset)).trim() || 'Untitled',
    media_url: pid ? `https://stream.mux.com/${pid}.m3u8` : '',
    video_type: 'MUX',
    quality: 'FHD',
    duration: Math.round(asset.duration || 0),
    short_description: '',
    long_description: '',
    thumbnail_url: thumbnailOverride || muxThumb,
    release_date: null,
    status: 'draft',
    tags: [],
    youtube_id: null,
    cf_stream_uid: null,
  }
}

export default function BulkCreateCatalogModal({ selectedAssets, muxLinks, onClose, onComplete }) {
  const { api } = useAuth()

  // Filter out assets already linked to a catalog item (silent skip)
  const eligible = useMemo(
    () => selectedAssets.filter(a => !muxLinks?.[a.id]?.linked),
    [selectedAssets, muxLinks]
  )
  const skippedCount = selectedAssets.length - eligible.length

  // Editable titles, keyed by asset.id, seeded from Mux meta/passthrough
  const [titles, setTitles] = useState(() => {
    const m = {}
    for (const a of eligible) m[a.id] = defaultTitle(a)
    return m
  })
  // Optional CF Image thumbnail URL per asset; empty = use Mux auto-thumbnail
  const [thumbnails, setThumbnails] = useState({})

  const [busy, setBusy] = useState(false)
  const [results, setResults] = useState([])
  const [status, setStatus] = useState('')

  // Existing catalog titles (lowercased + trimmed) for duplicate detection.
  // null = still loading; Set = loaded.
  const [existingTitles, setExistingTitles] = useState(null)
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await api('/vod')
        if (cancelled) return
        const set = new Set(
          (data.items || []).map(i => (i.title || '').trim().toLowerCase()).filter(Boolean)
        )
        setExistingTitles(set)
      } catch {
        if (!cancelled) setExistingTitles(new Set())
      }
    })()
    return () => { cancelled = true }
  }, [api])
  const titlesLoading = existingTitles === null

  // Count occurrences of each title across the batch so we can flag
  // duplicates *within* the same submission too.
  const inBatchCounts = useMemo(() => {
    const m = {}
    for (const a of eligible) {
      const t = (titles[a.id] || '').trim().toLowerCase()
      if (t) m[t] = (m[t] || 0) + 1
    }
    return m
  }, [titles, eligible])

  function getDuplicateState(assetId) {
    const t = (titles[assetId] || '').trim().toLowerCase()
    if (!t) return { isDup: false }
    if (existingTitles?.has(t)) return { isDup: true, reason: 'already exists in catalog' }
    if (inBatchCounts[t] > 1) return { isDup: true, reason: 'duplicate within this batch' }
    return { isDup: false }
  }

  const anyDuplicates = useMemo(
    () => eligible.some(a => getDuplicateState(a.id).isDup),
    [eligible, titles, existingTitles, inBatchCounts] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const allDone = busy === false && results.length === eligible.length && eligible.length > 0

  function updateTitle(assetId, value) {
    setTitles(prev => ({ ...prev, [assetId]: value }))
  }

  function updateThumbnail(assetId, value) {
    setThumbnails(prev => ({ ...prev, [assetId]: value }))
  }

  async function handleSubmit() {
    if (!eligible.length) return
    if (anyDuplicates) return  // belt-and-braces — button should already be disabled
    setBusy(true)
    setResults([])
    const out = []
    for (let i = 0; i < eligible.length; i++) {
      const asset = eligible[i]
      setStatus(`Creating ${i + 1} of ${eligible.length}…`)
      try {
        const res = await api('/vod/create-linked', {
          method: 'POST',
          body: JSON.stringify(buildPayload(asset, titles[asset.id], thumbnails[asset.id])),
        })
        out.push({ assetId: asset.id, ok: true, tbtvId: res.tbtv_id, title: titles[asset.id] })
      } catch (e) {
        out.push({ assetId: asset.id, ok: false, error: e.message, title: titles[asset.id] })
      }
      setResults([...out])
    }
    const ok = out.filter(r => r.ok).length
    const fail = out.length - ok
    setStatus(`Created ${ok}${fail ? `, ${fail} failed` : ''}`)
    setBusy(false)
    if (ok > 0) onComplete?.(ok, fail)
  }

  return (
    <div className="mux-modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="mux-modal" onClick={e => e.stopPropagation()}>
        <div className="mux-modal-header">
          <h3>Create Catalog Items from Mux</h3>
          {!busy && <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>}
        </div>

        <div className="mux-modal-body">
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            {eligible.length} item{eligible.length !== 1 ? 's' : ''} will be created as <strong>draft</strong>.
            {skippedCount > 0 && (
              <> {skippedCount} already linked to a catalog item — skipped.</>
            )}
          </div>

          {eligible.length === 0 ? (
            <div className="mux-restore-empty">
              All selected Mux assets are already linked to catalog items. Nothing to create.
            </div>
          ) : (
            <div className="mux-restore-list">
              {eligible.map((asset, idx) => {
                const r = results.find(x => x.assetId === asset.id)
                const dup = getDuplicateState(asset.id)
                return (
                  <div
                    key={asset.id}
                    className={`mux-restore-item ${r?.ok ? 'selected' : ''}`}
                    style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6, padding: '8px 10px' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 28, flexShrink: 0 }}>
                        {idx + 1}.
                      </span>
                      <input
                        type="text"
                        value={titles[asset.id] || ''}
                        onChange={e => updateTitle(asset.id, e.target.value)}
                        disabled={busy || r?.ok}
                        style={{
                          flex: 1,
                          borderColor: dup.isDup && !r?.ok ? '#ef4444' : undefined,
                        }}
                        placeholder="Title"
                      />
                      {r && (
                        r.ok ? (
                          <span className="mux-restore-result-icon" title={`Created ${r.tbtvId}`}>✓</span>
                        ) : (
                          <span className="mux-restore-error" title={r.error}>✕ {r.error}</span>
                        )
                      )}
                    </div>
                    {dup.isDup && !r?.ok && (
                      <div style={{ paddingLeft: 36, color: '#ef4444', fontSize: 11 }}>
                        Duplicate title — {dup.reason}.
                      </div>
                    )}
                    <div style={{ paddingLeft: 36 }}>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>
                        Thumbnail override (optional — defaults to Mux auto-thumbnail)
                      </div>
                      <ImagePicker
                        value={thumbnails[asset.id] || ''}
                        onChange={(v) => updateThumbnail(asset.id, v)}
                        placeholder="Paste URL or browse CF Images"
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {eligible.length > 0 && (
            <div className="mux-restore-action">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {busy ? status : `${eligible.length} draft catalog item${eligible.length !== 1 ? 's' : ''} will be created`}
                </span>
                {allDone ? (
                  <button className="btn-primary" onClick={onClose}>Done</button>
                ) : (
                  <button
                    className="btn-primary"
                    disabled={busy || anyDuplicates || titlesLoading}
                    onClick={handleSubmit}
                    title={
                      titlesLoading ? 'Checking for duplicate titles…' :
                      anyDuplicates ? 'Resolve duplicate titles before creating' : undefined
                    }
                  >
                    {busy ? 'Creating…' : `Create ${eligible.length}`}
                  </button>
                )}
              </div>
              {status && !busy && <div className="mux-restore-status">{status}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
